import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { UserModel } from '../models/user.model';
import { authActions } from './auth.actions';

export interface AuthState {
    user: UserModel | null;
    isSemiAuth: boolean;
    lastKnownAuthState: LastKnownAuthState;
    lastLoginType: LastLoginType;
    returnUrl: string;
    error: any;
    loading: boolean;
    loaded: boolean;
    impersonationHandled: boolean;
}

export enum LastKnownAuthState {
    Unknown,
    LoggedOut,
    SemiLoggedIn,
    FullyLoggedIn
}

export enum LastLoginType {
    Unknown,
    Manual,
    Auto
}

export const initialAuthState: AuthState = {
    user: undefined,
    isSemiAuth: undefined,
    lastKnownAuthState: LastKnownAuthState.Unknown,
    lastLoginType: LastLoginType.Unknown,
    returnUrl: undefined,
    error: null,
    loading: false,
    loaded: false,
    impersonationHandled: false
};

const authReducer = createReducer(
    initialAuthState,
    on(
        authActions.login,
        authActions.autologin,
        authActions.impersonate,
        (state): AuthState => ({ ...state, loading: true })
    ),
    on(
        authActions.loginSucceeded,
        (state, action): AuthState => ({
            ...state,
            user: action.user,
            isSemiAuth: null,
            lastKnownAuthState: LastKnownAuthState.FullyLoggedIn,
            lastLoginType: LastLoginType.Manual,
            loaded: true,
            loading: false
        })
    ),
    on(
        authActions.setReturnUrl,
        (state, action): AuthState => ({
            ...state,
            returnUrl: action.returnUrl
        })
    ),
    on(
        authActions.autologinSucceeded,
        (state, action): AuthState => ({
            ...state,
            user: action.user,
            lastKnownAuthState: action.isSemiAuth ? LastKnownAuthState.SemiLoggedIn : LastKnownAuthState.FullyLoggedIn,
            lastLoginType: LastLoginType.Auto,
            isSemiAuth: action.isSemiAuth,
            loaded: true,
            loading: false
        })
    ),
    on(
        authActions.impersonateSucceeded,
        (state, action): AuthState => ({
            ...state,
            user: action.user,
            loaded: true,
            loading: false,
            impersonationHandled: true
        })
    ),
    on(
        authActions.skipImpersonation,
        (state, action): AuthState => ({
            ...state,
            impersonationHandled: true
        })
    ),
    on(
        authActions.loginFailed,
        (state): AuthState => ({
            // do not change the user/loggedin state on failure,
            // as it can be semi auth before and if not user is null anyway
            ...state,
            loaded: true,
            loading: false
        })
    ),
    on(
        authActions.autologinFailed,
        (state): AuthState => ({
            ...state,
            user: null,
            lastKnownAuthState: LastKnownAuthState.LoggedOut,
            loaded: true,
            loading: false
        })
    ),
    on(
        authActions.impersonateFailed,
        (state, action): AuthState => ({
            ...state,
            error: action.error,
            loaded: true,
            loading: false
        })
    ),
    on(
        authActions.autologinCancelled,
        (state): AuthState => ({
            ...state,
            loading: false
        })
    ),
    on(
        authActions.logout,
        (state): AuthState => ({
            ...state,
            loaded: true,
            loading: true
        })
    ),
    on(
        authActions.interruptLogin,
        authActions.interruptLoginOnFirstRoute,
        (state, action): AuthState => ({
            // if 402 error (full auth required and user is semi auth)
            ...state,
            user: action.fullAuthRequired ? state.user : null,
            isSemiAuth: action.fullAuthRequired ? true : null,
            loaded: true,
            loading: false
        })
    ),
    on(
        authActions.logoutSucceeded,
        (state): AuthState => ({
            ...state,
            user: null,
            isSemiAuth: null,
            loaded: true,
            loading: false,
            impersonationHandled: false
        })
    ),
    on(
        authActions.logoutFailed,
        (state): AuthState => ({
            ...state,
            loaded: true,
            loading: false
        })
    )
);

export const authFeature = createFeature({
    name: 'auth',
    reducer: authReducer,
    extraSelectors: ({ selectAuthState, selectUser, selectLoaded, selectImpersonationHandled }) => {
        const selectUserFinishedLoading = createSelector(selectAuthState, state => state.loaded && !state.loading);

        const selectIsLoggedIn = createSelector(
            selectUser,
            selectLoaded,
            selectImpersonationHandled,
            (user, isLoaded, impersonationHandled) => (!isLoaded || !impersonationHandled ? undefined : !!user)
        );

        const selectIsFullyLoggedIn = createSelector(
            selectAuthState,
            selectIsLoggedIn,
            (state, isLoggedIn) => !state.isSemiAuth && isLoggedIn
        );

        const selectWasLoggedInBefore = createSelector(
            selectAuthState,
            selectIsLoggedIn,
            (state, isLoggedIn) =>
                (isLoggedIn === false && state.lastKnownAuthState === LastKnownAuthState.FullyLoggedIn) ||
                state.lastKnownAuthState === LastKnownAuthState.SemiLoggedIn
        );

        const selectIsImpersonating = createSelector(
            selectIsLoggedIn,
            selectUser,
            (isLoggedIn, user) => isLoggedIn && user.uid !== user.loggedInUid
        );

        const selectIsImpersonatingAllowed = createSelector(selectUser, user => user?.roles.includes('ROLE_ONBOARDER'));

        return {
            selectUserFinishedLoading,
            selectIsLoggedIn,
            selectIsFullyLoggedIn,
            selectWasLoggedInBefore,
            selectIsImpersonating,
            selectIsImpersonatingAllowed
        };
    }
});
